// src/components/shared/Calendar.jsx
// Unified ERP Calendar Component
// Combines:
// 1. Personal & Staff Attendance Tracking (Present, Absent, Leave, Hours, Punch-in/out, PDF Report)
// 2. Institutional & National Holidays (Public, Restricted, College, Exam, Cultural Events)
// 3. Admin Holiday Management (Bulk addition, inline edits, deletion)
// 4. Combined 12-Month Interactive Grid View

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { calendarAPI, attendanceAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';
import '../../styles/YearlyCalendar.css';

// ─── Constants & Configurations ───────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_INITIALS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const HOLIDAY_TYPES = [
  { value: 'PUBLIC',     label: 'Public Holiday',     color: '#ef4444' },
  { value: 'RESTRICTED', label: 'Restricted Holiday',  color: '#f97316' },
  { value: 'COLLEGE',    label: 'College Event',       color: '#3b82f6' },
  { value: 'EXAM',       label: 'Exam / Assessment',   color: '#8b5cf6' },
  { value: 'CULTURAL',   label: 'Cultural Event',      color: '#10b981' },
  { value: 'OTHER',      label: 'Other',               color: '#6b7280' },
];

const typeColorMap = Object.fromEntries(HOLIDAY_TYPES.map(t => [t.value, t.color]));

const ATTENDANCE_STATUS_CFG = {
  PRESENT: { color: '#16a34a', bg: '#ecfdf4', border: '#86efac', label: 'Present' },
  ABSENT:  { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', label: 'Absent'  },
  LEAVE:   { color: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'On Leave' },
  HOLIDAY: { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', label: 'Holiday' },
  FUTURE:  { color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', label: 'Future'  },
  BEFORE:  { color: '#cbd5e1', bg: '#f8fafc', border: '#e2e8f0', label: 'N/A'     },
};

const BLANK_HOLIDAY_ROW = () => ({
  id: Date.now() + Math.random(),
  name: '',
  date: '',
  holiday_type: 'PUBLIC',
  description: '',
  is_recurring: false,
});

const EMPTY_SINGLE_HOLIDAY = {
  name: '',
  date: '',
  holiday_type: 'PUBLIC',
  description: '',
  is_recurring: false,
};

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function firstDayOfMonth(year, month) {
  return new Date(year, month - 1, 1).getDay();
}

function isoDate(y, m, d) {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function fmtHours(h) {
  const n = parseFloat(h) || 0;
  return `${String(Math.floor(n)).padStart(2, '0')}h ${String(Math.round((n % 1) * 60)).padStart(2, '0')}m`;
}

function fmtDateLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function todayISO() {
  const d = new Date();
  return isoDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function firstOfYear(y) {
  return `${y}-01-01`;
}

// ─── jsPDF Lazy Loader & PDF Generator ────────────────────────────────────────
let _jsPDFLoaded = false;
function _loadScript(src) {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.onload = res;
    s.onerror = () => rej(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

async function ensureJsPDF() {
  if (_jsPDFLoaded) return;
  await _loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
  await _loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');
  _jsPDFLoaded = true;
}

async function generateAttendancePDF({ records, fromDate, toDate, userName, joiningDate }) {
  await ensureJsPDF();
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const PAGE_W  = 210;
  const MARGIN  = 14;

  const BLUE    = [37,  99, 235];
  const GREEN   = [22, 163,  74];
  const RED     = [220, 38,  38];
  const AMBER   = [217, 119,  6];
  const DARK    = [15,  23,  42];
  const MUTED   = [100, 116, 139];
  const WHITE   = [255, 255, 255];
  const DIVIDER = [226, 232, 240];

  // Header banner
  doc.setFillColor(...BLUE);
  doc.rect(0, 0, PAGE_W, 30, 'F');

  doc.setTextColor(...WHITE);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Institutional Attendance & Calendar Report', MARGIN, 13);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Period: ${fmtDateLabel(fromDate)}  to  ${fmtDateLabel(toDate)}  |  Member: ${userName || 'Employee'}`,
    MARGIN, 22
  );

  doc.text(
    `Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`,
    PAGE_W - MARGIN, 22, { align: 'right' }
  );

  const tableRows = records.map((r, i) => {
    let statusText = r.status || 'ABSENT';
    let textColor = DARK;
    let fill = [255, 255, 255];

    if (r.status === 'PRESENT') {
      textColor = GREEN;
      fill = [240, 253, 244];
    } else if (r.status === 'ABSENT') {
      textColor = RED;
      fill = [254, 242, 242];
    } else if (r.status === 'LEAVE') {
      textColor = AMBER;
      fill = [255, 251, 235];
    } else if (r.status === 'HOLIDAY') {
      textColor = BLUE;
      fill = [239, 246, 255];
    }

    return {
      idx: i + 1,
      date: fmtDateLabel(r.date),
      day: new Date(r.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short' }),
      status: { text: statusText, textColor, fill },
      shift: r.shift_name || 'Standard',
      pinTime: fmtTime(r.punch_in),
      poutTime: fmtTime(r.punch_out),
      hours: parseFloat(r.total_work_hours) > 0 ? fmtHours(r.total_work_hours) : '—',
      late: r.is_late ? 'Yes' : '—',
      leave: r.leave_type || '—',
    };
  });

  doc.autoTable({
    startY: 36,
    margin: { left: MARGIN, right: MARGIN },
    head: [['#', 'Date', 'Day', 'Status', 'Shift', 'Punch In', 'Punch Out', 'Hours', 'Late', 'Leave Type']],
    body: tableRows.map(r => [
      r.idx,
      r.date,
      r.day,
      r.status.text,
      r.shift,
      r.pinTime,
      r.poutTime,
      r.hours,
      r.late,
      r.leave,
    ]),
    headStyles: {
      fillColor: [...BLUE],
      textColor: [...WHITE],
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'center',
      cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [...DARK],
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 8 },
      1: { cellWidth: 26 },
      2: { halign: 'center', cellWidth: 12 },
      3: { halign: 'center', cellWidth: 20 },
      4: { cellWidth: 22 },
      5: { halign: 'center', cellWidth: 22 },
      6: { halign: 'center', cellWidth: 22 },
      7: { halign: 'center', cellWidth: 18 },
      8: { halign: 'center', cellWidth: 12 },
      9: { cellWidth: 'auto' },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didParseCell(data) {
      if (data.column.index === 3 && data.section === 'body') {
        const row = tableRows[data.row.index];
        if (row) {
          data.cell.styles.textColor = row.status.textColor;
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = row.status.fill;
        }
      }
    },
    didDrawPage(hookData) {
      const pg = hookData.pageNumber;
      const total = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      doc.text(`Page ${pg} of ${total}`, PAGE_W / 2, 292, { align: 'center' });
      doc.text('ERP Calendar & Attendance Report', MARGIN, 292);
      doc.setDrawColor(...DIVIDER);
      doc.setLineWidth(0.3);
      doc.line(MARGIN, 289, PAGE_W - MARGIN, 289);
    },
    tableLineColor: [...DIVIDER],
    tableLineWidth: 0.3,
    showHead: 'everyPage',
  });

  const filename = `attendance_report_${fromDate}_to_${toDate}.pdf`;
  doc.save(filename);
}

// ─── PDF Report Selector Panel ────────────────────────────────────────────────
function PDFReportPanel({ attendanceData, year, userId, joiningDate, onClose }) {
  const today = todayISO();
  const effectiveJoining = joiningDate || attendanceData?.joining_date || null;
  const initialFrom = (effectiveJoining && effectiveJoining.slice(0, 4) === String(year) && effectiveJoining > firstOfYear(year))
    ? effectiveJoining
    : firstOfYear(year);

  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(today);
  const [generating, setGenerating] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [pdfSuccess, setPdfSuccess] = useState('');

  useEffect(() => {
    const initF = (effectiveJoining && effectiveJoining.slice(0, 4) === String(year) && effectiveJoining > firstOfYear(year))
      ? effectiveJoining
      : firstOfYear(year);
    setFrom(initF);
    setTo(today);
  }, [year, today, effectiveJoining]);

  const handleGenerate = async () => {
    if (!from || !to) { setPdfError('Please select both From and To dates.'); return; }
    if (from > to) { setPdfError('"From" date must be before "To" date.'); return; }
    if (effectiveJoining && from < effectiveJoining) {
      setPdfError(`From date cannot precede employee's Date of Joining (${fmtDateLabel(effectiveJoining)}).`);
      return;
    }

    setPdfError('');
    setPdfSuccess('');
    setGenerating(true);

    try {
      let records = [];
      const fromYear = parseInt(from.slice(0, 4), 10);
      const toYear = parseInt(to.slice(0, 4), 10);

      if (fromYear === toYear && fromYear === year && attendanceData?.records) {
        records = (attendanceData.records || []).filter(r => r.date >= from && r.date <= to);
      } else {
        for (let y = fromYear; y <= toYear; y++) {
          const res = await attendanceAPI.getYearly(y, userId);
          const filtered = (res.records || []).filter(r => r.date >= from && r.date <= to);
          records = records.concat(filtered);
        }
      }

      if (records.length === 0) {
        setPdfError('No attendance records found for the selected date range.');
        setGenerating(false);
        return;
      }

      records.sort((a, b) => (a.date > b.date ? 1 : -1));

      await generateAttendancePDF({
        records,
        fromDate: from,
        toDate: to,
        userName: attendanceData?.userName || '',
        joiningDate: effectiveJoining,
      });

      setPdfSuccess(`Generated report with ${records.length} records.`);
    } catch (err) {
      console.error(err);
      setPdfError('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #bfdbfe',
      borderRadius: 12,
      padding: '16px 20px',
      marginBottom: 20,
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.08)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>📄</span>
          <div>
            <strong style={{ fontSize: 14, color: '#0f172a' }}>Export Attendance PDF Report</strong>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              Select date range to download an official attendance record
              {effectiveJoining && <span style={{ marginLeft: 6, color: '#2563eb', fontWeight: 600 }}>• Joined: {fmtDateLabel(effectiveJoining)}</span>}
            </div>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}>✕</button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>FROM DATE</label>
          <input
            type="date"
            value={from}
            min={effectiveJoining || undefined}
            max={to || today}
            onChange={e => setFrom(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>TO DATE</label>
          <input
            type="date"
            value={to}
            min={from}
            max={today}
            onChange={e => setTo(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
          />
        </div>

        {/* Preset quick buttons */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: 'This Month', act: () => {
              const n = new Date();
              const mStart = isoDate(n.getFullYear(), n.getMonth() + 1, 1);
              setFrom(effectiveJoining && effectiveJoining > mStart ? effectiveJoining : mStart);
              setTo(today);
            }},
            { label: 'Last Month', act: () => {
              const n = new Date();
              const lmStart = new Date(n.getFullYear(), n.getMonth() - 1, 1);
              const lmEnd = new Date(n.getFullYear(), n.getMonth(), 0);
              const startStr = isoDate(lmStart.getFullYear(), lmStart.getMonth() + 1, 1);
              const endStr = isoDate(lmEnd.getFullYear(), lmEnd.getMonth() + 1, lmEnd.getDate());
              setFrom(effectiveJoining && effectiveJoining > startStr ? effectiveJoining : startStr);
              setTo(endStr);
            }},
            { label: 'This Year', act: () => {
              const foy = firstOfYear(new Date().getFullYear());
              setFrom(effectiveJoining && effectiveJoining > foy ? effectiveJoining : foy);
              setTo(today);
            }},
          ].map(p => (
            <button
              key={p.label}
              onClick={() => { p.act(); setPdfError(''); setPdfSuccess(''); }}
              style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 12, cursor: 'pointer', fontWeight: 600, color: '#475569' }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <button
          onClick={handleGenerate}
          disabled={generating}
          style={{
            marginLeft: 'auto',
            padding: '8px 18px',
            borderRadius: 8,
            border: 'none',
            background: generating ? '#94a3b8' : '#2563eb',
            color: '#fff',
            fontWeight: 700,
            fontSize: 13,
            cursor: generating ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {generating ? 'Generating PDF…' : '📥 Download Report'}
        </button>
      </div>

      {pdfError && <div style={{ marginTop: 10, color: '#dc2626', fontSize: 12 }}>⚠️ {pdfError}</div>}
      {pdfSuccess && <div style={{ marginTop: 10, color: '#16a34a', fontSize: 12 }}>✓ {pdfSuccess}</div>}
    </div>
  );
}

// ─── Bulk Holiday Manager Component (Admin Tab) ───────────────────────────────
function BulkHolidayManagerView({ year, onHolidayChanged }) {
  const [rows, setRows] = useState([BLANK_HOLIDAY_ROW(), BLANK_HOLIDAY_ROW(), BLANK_HOLIDAY_ROW()]);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);
  const [calYear, setCalYear] = useState(year || new Date().getFullYear());
  const [existingHolidays, setExistingHolidays] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);

  const fetchExisting = useCallback(async (y) => {
    setLoadingList(true);
    try {
      const data = await calendarAPI.getHolidays({ year: y });
      setExistingHolidays(Array.isArray(data) ? data : (data.results || []));
    } catch {
      setExistingHolidays([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchExisting(calYear);
  }, [calYear, fetchExisting]);

  const updateRow = (id, field, value) =>
    setRows(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  const addRow = () => setRows(prev => [...prev, BLANK_HOLIDAY_ROW()]);
  const removeRow = id => {
    if (rows.length > 1) setRows(prev => prev.filter(r => r.id !== id));
  };
  const clearAll = () => setRows([BLANK_HOLIDAY_ROW(), BLANK_HOLIDAY_ROW(), BLANK_HOLIDAY_ROW()]);

  const handleBulkSave = async () => {
    const valid = rows.filter(r => r.name.trim() && r.date);
    if (!valid.length) {
      notify.error('Please fill at least one holiday with a name and date.');
      return;
    }
    if (!await confirmDialog(`Save ${valid.length} holiday(s)?`)) return;

    setSaving(true);
    setResults(null);
    const saved = [];
    const errors = [];

    for (const row of valid) {
      try {
        const res = await calendarAPI.createHoliday({
          name: row.name.trim(),
          date: row.date,
          holiday_type: row.holiday_type,
          description: (row.description || '').trim(),
          is_recurring: !!row.is_recurring,
        });
        saved.push(res);
      } catch (err) {
        errors.push({ name: row.name, date: row.date, error: err.response?.data?.detail || err.response?.data?.error || 'Failed' });
      }
    }

    setResults({ saved, errors });
    if (saved.length > 0) {
      clearAll();
      fetchExisting(calYear);
      if (onHolidayChanged) onHolidayChanged();
    }
    setSaving(false);
  };

  const startEdit = h => setEditingHoliday({ ...h });
  const cancelEdit = () => setEditingHoliday(null);
  const saveEdit = async () => {
    try {
      await calendarAPI.updateHoliday(editingHoliday.id, {
        name: editingHoliday.name,
        date: editingHoliday.date,
        holiday_type: editingHoliday.holiday_type,
        description: editingHoliday.description,
        is_recurring: editingHoliday.is_recurring,
      });
      setEditingHoliday(null);
      fetchExisting(calYear);
      if (onHolidayChanged) onHolidayChanged();
    } catch (e) {
      notify.error(e.response?.data?.error || 'Failed to update holiday.');
    }
  };

  const handleDelete = async (id, name) => {
    if (!await confirmDialog(`Delete holiday "${name}"?`)) return;
    try {
      await calendarAPI.deleteHoliday(id);
      fetchExisting(calYear);
      if (onHolidayChanged) onHolidayChanged();
    } catch {
      notify.error('Failed to delete holiday.');
    }
  };

  const validCount = rows.filter(r => r.name.trim() && r.date).length;

  return (
    <div style={{ background: '#ffffff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
      {/* Year Selection Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
            ⚙️ Institutional Holiday Management
          </h3>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            Add single or bulk holidays, manage recurring events, and audit institutional holidays.
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Year:</span>
          <button onClick={() => setCalYear(y => y - 1)} style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #cbd5e1', cursor: 'pointer' }}>‹</button>
          <strong style={{ fontSize: 15, color: '#0f172a', minWidth: 44, textAlign: 'center' }}>{calYear}</strong>
          <button onClick={() => setCalYear(y => y + 1)} style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #cbd5e1', cursor: 'pointer' }}>›</button>
        </div>
      </div>

      {results && (
        <div style={{
          marginBottom: 16,
          padding: '12px 16px',
          borderRadius: 8,
          background: results.errors.length > 0 ? '#fff7ed' : '#f0fdf4',
          border: `1px solid ${results.errors.length > 0 ? '#fed7aa' : '#bbf7d0'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            {results.saved.length > 0 && <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ {results.saved.length} holidays saved successfully. </span>}
            {results.errors.length > 0 && <span style={{ color: '#dc2626', fontWeight: 700 }}>✕ {results.errors.length} failed.</span>}
          </div>
          <button onClick={() => setResults(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>
      )}

      {/* Add Rows Section */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Add New Holidays (Bulk or Single)</h4>
          <button onClick={clearAll} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer' }}>🗑 Clear All</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((row, idx) => (
            <div key={row.id} style={{ display: 'grid', gridTemplateColumns: '32px 140px 1fr 160px 1fr 100px 36px', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textAlign: 'center' }}>#{idx + 1}</span>
              <input
                type="date"
                value={row.date}
                onChange={e => updateRow(row.id, 'date', e.target.value)}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
              />
              <input
                type="text"
                placeholder="Holiday Name *"
                value={row.name}
                onChange={e => updateRow(row.id, 'name', e.target.value)}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
              />
              <select
                value={row.holiday_type}
                onChange={e => updateRow(row.id, 'holiday_type', e.target.value)}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
              >
                {HOLIDAY_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Optional description / note"
                value={row.description}
                onChange={e => updateRow(row.id, 'description', e.target.value)}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={row.is_recurring}
                  onChange={e => updateRow(row.id, 'is_recurring', e.target.checked)}
                />
                Annual
              </label>
              <button
                onClick={() => removeRow(row.id)}
                disabled={rows.length === 1}
                style={{ background: '#fee2e2', border: 'none', borderRadius: 6, color: '#dc2626', cursor: rows.length === 1 ? 'not-allowed' : 'pointer', height: 30 }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <button
            onClick={addRow}
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
          >
            + Add Row
          </button>
          <button
            onClick={handleBulkSave}
            disabled={saving || validCount === 0}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              background: validCount > 0 ? '#16a34a' : '#94a3b8',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              cursor: validCount > 0 && !saving ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Saving…' : `💾 Save ${validCount > 0 ? validCount : ''} Holiday${validCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>

      {/* Existing Holidays List */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
            Existing Holidays for {calYear} ({existingHolidays.length})
          </h4>
        </div>

        {loadingList ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>Loading holidays…</div>
        ) : existingHolidays.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', background: '#f8fafc', borderRadius: 8 }}>
            No holidays registered for {calYear}.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {existingHolidays.map(h => (
              editingHoliday?.id === h.id ? (
                <div key={h.id} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 160px 100px 120px', gap: 8, padding: 8, background: '#f0fdf4', borderRadius: 6, border: '1px solid #86efac', alignItems: 'center' }}>
                  <input
                    type="date"
                    value={editingHoliday.date}
                    onChange={e => setEditingHoliday(p => ({ ...p, date: e.target.value }))}
                    style={{ padding: '4px 6px', fontSize: 12 }}
                  />
                  <input
                    type="text"
                    value={editingHoliday.name}
                    onChange={e => setEditingHoliday(p => ({ ...p, name: e.target.value }))}
                    style={{ padding: '4px 6px', fontSize: 12 }}
                  />
                  <select
                    value={editingHoliday.holiday_type}
                    onChange={e => setEditingHoliday(p => ({ ...p, holiday_type: e.target.value }))}
                    style={{ padding: '4px 6px', fontSize: 12 }}
                  >
                    {HOLIDAY_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <label style={{ fontSize: 11 }}>
                    <input
                      type="checkbox"
                      checked={editingHoliday.is_recurring}
                      onChange={e => setEditingHoliday(p => ({ ...p, is_recurring: e.target.checked }))}
                    /> Annual
                  </label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={saveEdit} style={{ padding: '4px 8px', borderRadius: 4, background: '#16a34a', color: '#fff', border: 'none', fontSize: 11, cursor: 'pointer' }}>✓ Save</button>
                    <button onClick={cancelEdit} style={{ padding: '4px 8px', borderRadius: 4, background: '#cbd5e1', color: '#334155', border: 'none', fontSize: 11, cursor: 'pointer' }}>✕</button>
                  </div>
                </div>
              ) : (
                <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontWeight: 700, color: '#0f172a', minWidth: 100 }}>{h.date}</span>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{h.name}</span>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 9999,
                      background: `${typeColorMap[h.holiday_type] || '#64748b'}18`,
                      color: typeColorMap[h.holiday_type] || '#64748b',
                    }}>
                      {HOLIDAY_TYPES.find(t => t.value === h.holiday_type)?.label || h.holiday_type}
                    </span>
                    {h.is_recurring && <span style={{ fontSize: 11, color: '#6366f1' }}>🔁 Annual</span>}
                    {h.description && <span style={{ fontSize: 11, color: '#64748b' }}>({h.description})</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => startEdit(h)} style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', fontSize: 11, cursor: 'pointer' }}>✎ Edit</button>
                    <button onClick={() => handleDelete(h.id, h.name)} style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontSize: 11, cursor: 'pointer' }}>✕ Delete</button>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN UNIFIED CALENDAR COMPONENT ──────────────────────────────────────────
export default function Calendar({ userId = null, userName = null }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());

  // Sub-view modes: 'COMBINED' | 'ATTENDANCE' | 'HOLIDAYS' | 'ADMIN_MANAGE'
  const [viewMode, setViewMode] = useState('COMBINED');

  // Holidays state
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(true);
  const [holidayError, setHolidayError] = useState(null);

  // Attendance state
  const [attendanceData, setAttendanceData] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [attendanceError, setAttendanceError] = useState(null);

  // PDF report toggle
  const [showPDFPanel, setShowPDFPanel] = useState(false);

  // Single Holiday Modal (Admin)
  const [modalOpen, setModalOpen] = useState(false);
  const [editingHolidayId, setEditingHolidayId] = useState(null);
  const [singleHolidayForm, setSingleHolidayForm] = useState(EMPTY_SINGLE_HOLIDAY);
  const [savingHoliday, setSavingHoliday] = useState(false);
  const [holidayFormError, setHolidayFormError] = useState('');

  // Selected Day Inspection Tooltip / Popover
  const [tooltip, setTooltip] = useState(null); // { dateStr, attendanceRec, dayHolidays }
  const [popoverPos, setPopoverPos] = useState({ x: 0, y: 0 });
  const calendarRootRef = useRef(null);
  const activeAnchorRef = useRef(null);
  const popoverRef = useRef(null);

  // ─── Fetch Holidays ────────────────────────────────────────────────────────
  const fetchHolidays = useCallback(async () => {
    setLoadingHolidays(true);
    setHolidayError(null);
    try {
      const data = await calendarAPI.getHolidays({ year });
      const list = Array.isArray(data) ? data : (data.results || []);
      setHolidays(list);
    } catch {
      setHolidayError('Failed to load holidays.');
    } finally {
      setLoadingHolidays(false);
    }
  }, [year]);

  // ─── Fetch Attendance ──────────────────────────────────────────────────────
  const fetchAttendance = useCallback(async () => {
    setLoadingAttendance(true);
    setAttendanceError(null);
    try {
      const res = await attendanceAPI.getYearly(year, userId);
      setAttendanceData({ ...res, userName: userName || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username });
    } catch {
      // Non-staff or students might not have attendance records
      setAttendanceData(null);
    } finally {
      setLoadingAttendance(false);
    }
  }, [year, userId, userName, user]);

  useEffect(() => {
    fetchHolidays();
    fetchAttendance();
  }, [fetchHolidays, fetchAttendance]);

  // Lookup maps
  const holidayMap = {};
  holidays.forEach(h => {
    const k = h.date;
    if (!holidayMap[k]) holidayMap[k] = [];
    holidayMap[k].push(h);
  });

  const attendanceRecordMap = {};
  (attendanceData?.records || []).forEach(r => {
    attendanceRecordMap[r.date] = r;
  });

  const joiningDateStr =
    attendanceData?.joining_date ||
    user?.profile?.joining_date ||
    (user?.date_joined ? user.date_joined.split('T')[0] : null);

  // Helper to classify attendance on a date
  const classifyAttendance = dateStr => {
    if (joiningDateStr && dateStr < joiningDateStr) return 'BEFORE';
    const dateObj = new Date(dateStr + 'T00:00:00');
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (dateObj > todayDate) return 'FUTURE';
    const rec = attendanceRecordMap[dateStr];
    return rec ? rec.status : 'ABSENT';
  };

  // Holiday counters
  const holidayCounts = {};
  HOLIDAY_TYPES.forEach(t => { holidayCounts[t.value] = 0; });
  holidays.forEach(h => {
    if (joiningDateStr && h.date < joiningDateStr && viewMode !== 'HOLIDAYS' && viewMode !== 'ADMIN_MANAGE') {
      return;
    }
    if (holidayCounts[h.holiday_type] !== undefined) holidayCounts[h.holiday_type]++;
  });

  const attendanceSummary = attendanceData?.summary || {};

  // ─── Day Inspection Popover ────────────────────────────────────────────────
  const updatePopoverPos = useCallback((anchorEl) => {
    const target = anchorEl || activeAnchorRef.current;
    if (!target || !calendarRootRef.current) return;
    const rootRect = calendarRootRef.current.getBoundingClientRect();
    const cellRect = target.getBoundingClientRect();
    const popWidth = 280;
    const rootWidth = calendarRootRef.current.offsetWidth || rootRect.width;

    // Calculate x relative to calendarRootRef
    let x = cellRect.left - rootRect.left;
    if (x + popWidth > rootWidth - 8) {
      x = Math.max(8, (cellRect.right - rootRect.left) - popWidth);
    }
    x = Math.max(8, x);

    // Calculate y relative to calendarRootRef
    const popHeight = popoverRef.current ? popoverRef.current.offsetHeight : 240;
    const spaceBelow = window.innerHeight - cellRect.bottom;
    let y;
    if (spaceBelow < popHeight + 12 && cellRect.top > popHeight + 12) {
      // Place above the cell
      y = Math.max(4, (cellRect.top - rootRect.top) - popHeight - 6);
    } else {
      // Place below the cell
      y = (cellRect.bottom - rootRect.top) + 6;
    }

    setPopoverPos({ x, y });
  }, []);

  const handleDayClick = (dateStr, e) => {
    e.stopPropagation();
    if (joiningDateStr && dateStr < joiningDateStr) return;
    const anchorEl = e.currentTarget;
    activeAnchorRef.current = anchorEl;
    updatePopoverPos(anchorEl);
    setTooltip({
      dateStr,
      attendanceRec: attendanceRecordMap[dateStr] || null,
      dayHolidays: holidayMap[dateStr] || [],
    });
  };

  useEffect(() => {
    if (tooltip && activeAnchorRef.current) {
      updatePopoverPos(activeAnchorRef.current);
    }
  }, [tooltip, updatePopoverPos]);

  useEffect(() => {
    const handleResize = () => {
      if (activeAnchorRef.current) {
        updatePopoverPos(activeAnchorRef.current);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updatePopoverPos]);

  useEffect(() => {
    const close = () => {
      setTooltip(null);
      activeAnchorRef.current = null;
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // ─── Admin Single Holiday Modal ────────────────────────────────────
  const openCreateHoliday = (dateStr = '') => {
    setEditingHolidayId(null);
    setSingleHolidayForm({ ...EMPTY_SINGLE_HOLIDAY, date: dateStr });
    setHolidayFormError('');
    setModalOpen(true);
  };

  const openEditHoliday = (h, e) => {
    if (e) e.stopPropagation();
    setTooltip(null);
    setEditingHolidayId(h.id);
    setSingleHolidayForm({
      name: h.name,
      date: h.date,
      holiday_type: h.holiday_type,
      description: h.description || '',
      is_recurring: !!h.is_recurring,
    });
    setHolidayFormError('');
    setModalOpen(true);
  };

  const handleSaveHoliday = async () => {
    if (!singleHolidayForm.name.trim()) { setHolidayFormError('Holiday name is required.'); return; }
    if (!singleHolidayForm.date) { setHolidayFormError('Date is required.'); return; }
    setSavingHoliday(true);
    setHolidayFormError('');
    try {
      if (editingHolidayId) {
        await calendarAPI.updateHoliday(editingHolidayId, singleHolidayForm);
      } else {
        await calendarAPI.createHoliday(singleHolidayForm);
      }
      setModalOpen(false);
      fetchHolidays();
    } catch (err) {
      setHolidayFormError(err.response?.data?.error || err.response?.data?.detail || 'Failed to save holiday.');
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (id, name, e) => {
    if (e) e.stopPropagation();
    if (!await confirmDialog(`Delete holiday "${name}"?`)) return;
    try {
      await calendarAPI.deleteHoliday(id);
      setTooltip(null);
      fetchHolidays();
    } catch {
      notify.error('Failed to delete holiday.');
    }
  };

  // ─── Month Grid Cell Renderer ──────────────────────────────────────────────
  const renderMonth = monthIdx => {
    const m = monthIdx + 1;
    const totalDays = daysInMonth(year, m);
    const startDay = firstDayOfMonth(year, m);
    const cells = [];

    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === m;

    return (
      <div key={m} style={{
        background: '#ffffff',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        padding: '14px 12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Month Header */}
        <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: '#0f172a',
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{MONTH_NAMES[monthIdx]}</span>
          {isCurrentMonth && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '2px 6px', borderRadius: 8 }}>
              Current
            </span>
          )}
        </div>

        {/* Day Name Labels */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
          {DAY_INITIALS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#94a3b8' }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day Cells Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {cells.map((day, idx) => {
            if (day === null) return <div key={`b-${idx}`} style={{ aspectRatio: '1' }} />;

            const dateStr = isoDate(year, m, day);
            const isBefore = !!(joiningDateStr && dateStr < joiningDateStr);
            const dayHolidays = holidayMap[dateStr] || [];
            const hasHolidays = !isBefore && dayHolidays.length > 0;
            const isToday = isCurrentMonth && today.getDate() === day;
            const isSunday = (startDay + day - 1) % 7 === 0;

            // Attendance styling
            const attStatus = isBefore ? 'BEFORE' : classifyAttendance(dateStr);
            const attCfg = ATTENDANCE_STATUS_CFG[attStatus] || ATTENDANCE_STATUS_CFG.ABSENT;
            const attRecord = isBefore ? null : attendanceRecordMap[dateStr];

            const isSelected = tooltip?.dateStr === dateStr;

            // Decide styling based on viewMode
            let cellBg = '#ffffff';
            let cellBorder = isSelected ? '2px solid #2563eb' : (isToday ? '2px solid #2563eb' : '#f1f5f9');
            let textColor = isSunday ? '#ef4444' : '#1e293b';
            let cursor = 'pointer';
            let opacity = 1;

            if (isBefore) {
              cellBg = '#f8fafc';
              cellBorder = '1px dashed #cbd5e1';
              textColor = '#94a3b8';
              cursor = 'not-allowed';
              opacity = 0.45;
            } else if (viewMode === 'ATTENDANCE' || viewMode === 'COMBINED') {
              if (attStatus !== 'FUTURE' && attRecord) {
                cellBg = isSelected ? '#dbeafe' : attCfg.bg;
                cellBorder = isSelected ? '2px solid #1d4ed8' : (isToday ? '2px solid #2563eb' : `1px solid ${attCfg.border}`);
                textColor = attCfg.color;
              } else if (isSelected) {
                cellBg = '#eff6ff';
              }
            } else if (isSelected) {
              cellBg = '#eff6ff';
            }

            return (
              <div
                key={day}
                onClick={isBefore ? undefined : e => handleDayClick(dateStr, e)}
                style={{
                  aspectRatio: '1',
                  minHeight: 28,
                  borderRadius: 6,
                  background: cellBg,
                  border: cellBorder,
                  boxShadow: isSelected ? '0 0 0 2px rgba(37, 99, 235, 0.4)' : 'none',
                  zIndex: isSelected ? 2 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  cursor,
                  opacity,
                  transition: 'transform .1s, box-shadow .1s',
                }}
                title={isBefore ? `Prior to Joining Date (${fmtDateLabel(joiningDateStr)})` : (hasHolidays ? `${dayHolidays.map(h => h.name).join(', ')}` : (attRecord ? `${attRecord.status}` : dateStr))}
              >
                <span style={{ fontSize: 11, fontWeight: isToday ? 800 : 600, color: textColor, lineHeight: 1 }}>
                  {day}
                </span>

                {/* Holiday indicator dot / chip */}
                {hasHolidays && (
                  <div style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                    {dayHolidays.slice(0, 2).map(h => (
                      <span
                        key={h.id}
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          backgroundColor: typeColorMap[h.holiday_type] || '#ef4444',
                        }}
                      />
                    ))}
                    {dayHolidays.length > 2 && (
                      <span style={{ fontSize: 8, fontWeight: 800, color: '#ef4444' }}>+</span>
                    )}
                  </div>
                )}

                {/* Late attendance dot */}
                {!isBefore && attRecord?.is_late && (
                  <span style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: '#d97706',
                  }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Holiday list chips under the month */}
        {Object.keys(holidayMap)
          .filter(d => {
            if (!d.startsWith(`${year}-${String(m).padStart(2, '0')}-`)) return false;
            if (joiningDateStr && d < joiningDateStr && viewMode !== 'HOLIDAYS' && viewMode !== 'ADMIN_MANAGE') {
              return false; // Exclude pre-joining holidays from employee calendar
            }
            return true;
          })
          .sort()
          .map(d => holidayMap[d].map(h => (
            <div
              key={h.id}
              onClick={e => handleDayClick(d, e)}
              style={{
                marginTop: 4,
                padding: '3px 6px',
                borderRadius: 4,
                background: '#f8fafc',
                borderLeft: `3px solid ${typeColorMap[h.holiday_type] || '#ef4444'}`,
                fontSize: 11,
                color: '#334155',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontWeight: 700, color: '#0f172a', marginRight: 4 }}>{d.slice(8)}:</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{h.name}</span>
            </div>
          )))}
      </div>
    );
  };

  return (
    <div ref={calendarRootRef} style={{ minHeight: '100%', background: '#f8fafc', padding: 4, position: 'relative' }}>
      {/* ─── Top Control Bar ─── */}
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
          {/* Year Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => setYear(y => y - 1)}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer', fontSize: 16, fontWeight: 700 }}
            >
              ‹
            </button>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', minWidth: 80, textAlign: 'center' }}>
              {year}
            </h2>
            <button
              onClick={() => setYear(y => y + 1)}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer', fontSize: 16, fontWeight: 700 }}
            >
              ›
            </button>
            {year !== today.getFullYear() && (
              <button
                onClick={() => setYear(today.getFullYear())}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f1f5f9', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569' }}
              >
                Today's Year
              </button>
            )}
          </div>

          {/* Sub-view Mode Switcher Tabs */}
          <div style={{ display: 'flex', background: '#f1f5f9', padding: 4, borderRadius: 10, gap: 4, flexWrap: 'wrap' }}>
            <button
              onClick={() => setViewMode('COMBINED')}
              style={{
                padding: '6px 14px',
                borderRadius: 7,
                border: 'none',
                background: viewMode === 'COMBINED' ? '#ffffff' : 'transparent',
                color: viewMode === 'COMBINED' ? '#0f172a' : '#64748b',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                boxShadow: viewMode === 'COMBINED' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              🗓️ All-in-One Calendar
            </button>

            <button
              onClick={() => setViewMode('ATTENDANCE')}
              style={{
                padding: '6px 14px',
                borderRadius: 7,
                border: 'none',
                background: viewMode === 'ATTENDANCE' ? '#ffffff' : 'transparent',
                color: viewMode === 'ATTENDANCE' ? '#0f172a' : '#64748b',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                boxShadow: viewMode === 'ATTENDANCE' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              📊 Attendance & Reports
            </button>

            <button
              onClick={() => setViewMode('HOLIDAYS')}
              style={{
                padding: '6px 14px',
                borderRadius: 7,
                border: 'none',
                background: viewMode === 'HOLIDAYS' ? '#ffffff' : 'transparent',
                color: viewMode === 'HOLIDAYS' ? '#0f172a' : '#64748b',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                boxShadow: viewMode === 'HOLIDAYS' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              🎉 Holidays ({holidays.length})
            </button>

            {isAdmin && (
              <button
                onClick={() => setViewMode('ADMIN_MANAGE')}
                style={{
                  padding: '6px 14px',
                  borderRadius: 7,
                  border: 'none',
                  background: viewMode === 'ADMIN_MANAGE' ? '#ffffff' : 'transparent',
                  color: viewMode === 'ADMIN_MANAGE' ? '#b45309' : '#64748b',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                  boxShadow: viewMode === 'ADMIN_MANAGE' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                ⚙️ Manage Holidays
              </button>
            )}
          </div>

          {/* Quick Action Buttons */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => setShowPDFPanel(p => !p)}
              style={{
                padding: '7px 14px',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                background: showPDFPanel ? '#eff6ff' : '#ffffff',
                color: showPDFPanel ? '#2563eb' : '#334155',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span>📄</span> {showPDFPanel ? 'Hide PDF Report' : 'PDF Export'}
            </button>

            {isAdmin && (
              <button
                onClick={() => openCreateHoliday()}
                style={{
                  padding: '7px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#2563eb',
                  color: '#ffffff',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                + Add Holiday
              </button>
            )}
          </div>
        </div>

        {/* ─── Metric & Summary Ribbon ─── */}
        <div style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: '1px solid #f1f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}>
          {/* Attendance Stats Pills */}
          {attendanceData && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12, color: '#166534', fontWeight: 700 }}>
                <span>✓</span> Present: {attendanceSummary.total_present || 0}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: 12, color: '#991b1b', fontWeight: 700 }}>
                <span>✕</span> Absent: {attendanceSummary.total_absent || 0}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fde68a', fontSize: 12, color: '#92400e', fontWeight: 700 }}>
                <span>🌴</span> Leave: {attendanceSummary.total_leave || 0}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#2563eb', marginLeft: 4 }}>
                {attendanceSummary.attendance_pct != null ? `${attendanceSummary.attendance_pct}% Attendance` : ''}
              </div>
              {joiningDateStr && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12, color: '#1d4ed8', fontWeight: 700 }}>
                  <span>🗓️</span> Joined: {fmtDateLabel(joiningDateStr)}
                </div>
              )}
            </div>
          )}

          {/* Holiday Type Legend Dots */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {HOLIDAY_TYPES.map(t => (
              <div key={t.value} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: t.color }} />
                <span>{t.label}</span>
                <strong style={{ color: '#0f172a' }}>({holidayCounts[t.value] || 0})</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── PDF Report Panel (collapsible) ─── */}
      {showPDFPanel && (
        <PDFReportPanel
          attendanceData={attendanceData}
          year={year}
          userId={userId}
          joiningDate={joiningDateStr}
          onClose={() => setShowPDFPanel(false)}
        />
      )}

      {/* ─── ADMIN BULK HOLIDAY MANAGEMENT VIEW ─── */}
      {viewMode === 'ADMIN_MANAGE' && isAdmin ? (
        <BulkHolidayManagerView year={year} onHolidayChanged={fetchHolidays} />
      ) : (
        /* ─── 12-MONTH CALENDAR GRID ─── */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          {Array.from({ length: 12 }, (_, i) => renderMonth(i))}
        </div>
      )}

      {/* ─── DAY CLICK INSPECTION POPOVER ─── */}
      {tooltip && (
        <div
          ref={popoverRef}
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: popoverPos.y,
            left: popoverPos.x,
            zIndex: 9999,
            background: '#ffffff',
            borderRadius: 12,
            border: '1px solid #cbd5e1',
            boxShadow: '0 12px 30px -5px rgba(0,0,0,0.18), 0 8px 10px -6px rgba(0,0,0,0.1)',
            width: 280,
            padding: 14,
            animation: 'fadeIn 0.12s ease-out',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 6, marginBottom: 8 }}>
            <strong style={{ fontSize: 13, color: '#0f172a' }}>📅 {fmtDateLabel(tooltip.dateStr)}</strong>
            <button onClick={() => setTooltip(null)} style={{ background: 'none', border: 'none', fontSize: 16, color: '#64748b', cursor: 'pointer' }}>×</button>
          </div>

          {/* Attendance Section */}
          {tooltip.attendanceRec ? (
            <div style={{ marginBottom: 10, padding: 8, background: '#f8fafc', borderRadius: 8, fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: '#64748b' }}>Attendance:</span>
                <strong style={{
                  color: ATTENDANCE_STATUS_CFG[tooltip.attendanceRec.status]?.color || '#0f172a',
                }}>
                  {tooltip.attendanceRec.status} {tooltip.attendanceRec.is_late ? '(Late)' : ''}
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569' }}>
                <span>Punch In:</span> <strong>{fmtTime(tooltip.attendanceRec.punch_in)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569' }}>
                <span>Punch Out:</span> <strong>{fmtTime(tooltip.attendanceRec.punch_out)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569' }}>
                <span>Work Hours:</span> <strong>{fmtHours(tooltip.attendanceRec.total_work_hours)}</strong>
              </div>
              {tooltip.attendanceRec.leave_type && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#b45309' }}>
                  <span>Leave Type:</span> <strong>{tooltip.attendanceRec.leave_type}</strong>
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8, fontStyle: 'italic' }}>
              No punch / attendance record logged for this day.
            </div>
          )}

          {/* Holidays Section */}
          {tooltip.dayHolidays.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {tooltip.dayHolidays.map(h => (
                <div key={h.id} style={{ padding: 8, background: '#eff6ff', borderRadius: 8, borderLeft: `3px solid ${typeColorMap[h.holiday_type] || '#2563eb'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 12, color: '#1e3a8a' }}>{h.name}</strong>
                    <span style={{ fontSize: 10, fontWeight: 700, color: typeColorMap[h.holiday_type] }}>
                      {HOLIDAY_TYPES.find(t => t.value === h.holiday_type)?.label}
                    </span>
                  </div>
                  {h.description && <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{h.description}</div>}
                  {h.is_recurring && <div style={{ fontSize: 10, color: '#6366f1', marginTop: 2 }}>🔁 Recurring annually</div>}

                  {isAdmin && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, borderTop: '1px solid #dbeafe', paddingTop: 4 }}>
                      <button onClick={e => openEditHoliday(h, e)} style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: '#fff', border: '1px solid #cbd5e1', cursor: 'pointer' }}>✎ Edit</button>
                      <button onClick={e => handleDeleteHoliday(h.id, h.name, e)} style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626', cursor: 'pointer' }}>✕ Delete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No institutional holidays scheduled.</div>
          )}

          {/* Admin Add Holiday on this date */}
          {isAdmin && tooltip.dayHolidays.length === 0 && (
            <button
              onClick={() => {
                const d = tooltip.dateStr;
                setTooltip(null);
                openCreateHoliday(d);
              }}
              style={{
                marginTop: 8,
                width: '100%',
                padding: '6px',
                borderRadius: 6,
                border: '1px dashed #2563eb',
                background: '#eff6ff',
                color: '#1d4ed8',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              + Add Holiday on this Date
            </button>
          )}
        </div>
      )}

      {/* ─── CREATE / EDIT SINGLE HOLIDAY MODAL (Admin) ─── */}
      {modalOpen && isAdmin && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(2px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }} onClick={() => setModalOpen(false)}>
          <div style={{
            background: '#ffffff',
            borderRadius: 14,
            width: '100%',
            maxWidth: 480,
            padding: 22,
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                {editingHolidayId ? 'Edit Holiday' : 'Add New Holiday'}
              </h3>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>

            {holidayFormError && (
              <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', fontSize: 12 }}>
                ⚠️ {holidayFormError}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  Holiday Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Independence Day, Annual Sports Day..."
                  value={singleHolidayForm.name}
                  onChange={e => setSingleHolidayForm(p => ({ ...p, name: e.target.value }))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    Date <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={singleHolidayForm.date}
                    onChange={e => setSingleHolidayForm(p => ({ ...p, date: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    Type
                  </label>
                  <select
                    value={singleHolidayForm.holiday_type}
                    onChange={e => setSingleHolidayForm(p => ({ ...p, holiday_type: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  >
                    {HOLIDAY_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  Description / Remarks
                </label>
                <textarea
                  rows={2}
                  placeholder="Optional notes or details..."
                  value={singleHolidayForm.description}
                  onChange={e => setSingleHolidayForm(p => ({ ...p, description: e.target.value }))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={singleHolidayForm.is_recurring}
                  onChange={e => setSingleHolidayForm(p => ({ ...p, is_recurring: e.target.checked }))}
                />
                <span>Recurring every year on this date</span>
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: 13, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveHoliday}
                  disabled={savingHoliday}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#ffffff', fontWeight: 700, fontSize: 13, cursor: savingHoliday ? 'not-allowed' : 'pointer' }}
                >
                  {savingHoliday ? 'Saving…' : editingHolidayId ? 'Update Holiday' : 'Add Holiday'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
